import { useMemo, useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Upload, FileText, ArrowRight, CheckCircle2, CircleHelp,
  Wand2, MessageSquareQuote, AlertTriangle, Workflow, Mail, ShieldCheck, Clipboard,
} from "lucide-react";
import { runOnboardingAgent } from "../lib/onboardingAgent";
import { buildFoyrCaptureBookmarklet } from "../lib/foyrCaptureBookmarklet";
import { buildFoyrNeoPushBookmarklet } from "../lib/foyrNeoPushBookmarklet";
import { DemoRunButton } from "../components/onboard/DemoRunButton";
import { extractFromBrief, SAMPLE_BRIEF } from "../lib/ingest/extract";
import { REQUIRED_FIELDS } from "../lib/ingest/required";
import type { IngestPlan, IngestField, ConfidenceBand } from "../lib/ingest/types";
import { detectIntakePath, type IntakeDetection } from "../lib/intakeDetect";
import { OnboardingMagic } from "../components/onboard/OnboardingMagic";
import foyrSample from "../data/samples/foyr-classic-living-office.json";
import { env } from "../lib/env";
import { cls } from "../lib/cls";

/* Client-parseable plain-text formats. PDF / DOCX need a server
 * route (MinerU on DGX) — we fail loudly with a structured message
 * rather than silently dropping the user's file. CLAUDE.md §5. */
const TEXT_FILE_RE = /\.(txt|md|markdown|csv|json|tsv|log|html|htm|rtf)$/i;
const MAX_BRIEF_BYTES = 256 * 1024;   // 256 KB — bigger than any real brief.

/* ──────────────────────────────────────────────────────────────────
   Auto-onboarding wizard.
   Drop a brief → extractor maps to ontology → confidence-gated
   ingest → clarifying questions for low-confidence fields.
   ────────────────────────────────────────────────────────────────── */

export default function Onboard(): JSX.Element {
  const nav = useNavigate();
  const [brief, setBrief] = useState("");
  const [plan, setPlan] = useState<IngestPlan | null>(null);
  const [working, setWorking] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState(false);
  const [uploadInfo, setUploadInfo] = useState<{ name: string; bytes: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [detection, setDetection] = useState<IntakeDetection | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function runDetection(text: string, filename: string, bytes: number): void {
    if (!text.trim()) { setDetection(null); return; }
    setDetection(detectIntakePath({ text, filename, bytes }));
  }

  function loadSample(): void {
    setBrief(SAMPLE_BRIEF);
    setPlan(null);
    setUploadInfo(null);
    setUploadError(null);
    runDetection(SAMPLE_BRIEF, "sample-brief.txt", SAMPLE_BRIEF.length);
  }

  const [magicKey, setMagicKey] = useState<string | null>(null);

  async function importFoyrFromUrl(foyrProjectId: string, foyrUrl: string, meta: { name: string; client: string; city: string; bhk: string; budgetINR: number; deadline: string; bundleJson?: string }): Promise<void> {
    // The designer pasted their real Foyr Neo editor URL and named
    // the project. We create a *linked* project pointing back to the
    // Foyr URL with solid metadata — Foyr connector will overwrite
    // these values when the project bundle ingests.
    const id = `foyr-${foyrProjectId}`;
    const newProject = {
      id,
      name: meta.name,
      city: meta.city,
      client: meta.client,
      client_email: "—",
      bhk: meta.bhk,
      budgetINR: meta.budgetINR,
      deadline: meta.deadline,
      alerts: 0,
      status: "draft",
      thumbnailKey: "livingSofa",
      source: foyrUrl,
      foyr_project_id: foyrProjectId,
      created_at: new Date().toISOString(),
    };
    try {
      const raw = localStorage.getItem(env.VITE_LS_USER_PROJECTS);
      const arr = (raw ? JSON.parse(raw) : []) as { id: string }[];
      const filtered = Array.isArray(arr) ? arr.filter((p) => p.id !== id) : [];
      filtered.push(newProject);
      localStorage.setItem(env.VITE_LS_USER_PROJECTS, JSON.stringify(filtered));
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT, id);
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT_LEGACY, id);
    } catch { /* ignore */ }

    // Two ingest sources, in order:
    //   1. Designer pasted bundle JSON into the textarea (explicit).
    //   2. Cloudflare KV has a bundle the extension already forwarded
    //      for this Foyr project id — we fetch + ingest automatically.
    let bundleText: string | null = null;
    if (meta.bundleJson && meta.bundleJson.trim()) {
      bundleText = meta.bundleJson;
    } else {
      try {
        const r = await fetch(`/api/foyr-bundle/${foyrProjectId}`);
        if (r.ok) {
          const data = await r.json() as { ok: boolean; bundle?: string };
          if (data.ok && data.bundle) bundleText = data.bundle;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[FoyrMind] KV fetch failed — continuing without bundle ingest", err);
      }
    }
    if (bundleText) {
      try {
        const parsed = JSON.parse(bundleText) as Record<string, unknown>;
        const result = runOnboardingAgent(parsed, { id, name: meta.name });
        const gRaw = localStorage.getItem(env.VITE_LS_PROJECT_GRAPHS);
        const gMap = (gRaw ? JSON.parse(gRaw) : {}) as Record<string, unknown>;
        gMap[id] = { graph: result.graph, schema: result.schema, trace: result.trace, stats: result.stats, ingested_at: new Date().toISOString() };
        localStorage.setItem(env.VITE_LS_PROJECT_GRAPHS, JSON.stringify(gMap));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[FoyrMind] bundle parse / mapping failed", err);
      }
    }
    try { window.dispatchEvent(new Event("foyrmind:projectChanged")); } catch { /* ignore */ }
    nav("/", { replace: true });
  }

  function commitFoyrSample(name: string): void {
    // 1. Stash the verified-from-DOM ingest with the user's chosen
    //    project name. The cockpit's project switcher merges
    //    VITE_LS_USER_PROJECTS into the static PROJECTS list.
    // 2. Mark this new project as active so the cockpit opens on it.
    // CLAUDE.md §1 — every storage key and every field below is
    // either env-driven or derived from the verified payload.
    const id = `up-${Date.now().toString(36)}`;
    const verified = (foyrSample as { verified_from_rendered_dom_2026_05_17?: { breadcrumb_text?: string }; provenance: { source_url: string } });
    const verifiedBreadcrumb = verified.verified_from_rendered_dom_2026_05_17?.breadcrumb_text ?? null;
    const newProject = {
      id,
      name,
      city: verifiedBreadcrumb ?? "—",
      client: "Designer-confirmed on intake",
      client_email: "—",
      bhk: "—",
      budgetINR: 0,
      deadline: "",
      alerts: 0,
      status: "draft",
      thumbnailKey: "livingSofa",
      source: verified.provenance.source_url,
      created_at: new Date().toISOString(),
    };
    try {
      const raw = localStorage.getItem(env.VITE_LS_USER_PROJECTS);
      const arr: unknown[] = raw ? JSON.parse(raw) : [];
      arr.push(newProject);
      localStorage.setItem(env.VITE_LS_USER_PROJECTS, JSON.stringify(arr));
      localStorage.setItem(env.VITE_LS_INGEST_LATEST, JSON.stringify({
        plan_id: `foyr-magic-${Date.now()}`,
        project_id: id,
        project_name: name,
        source: verified.provenance.source_url,
        payload: foyrSample,
      }));
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT, id);
      localStorage.setItem(env.VITE_LS_ACTIVE_PROJECT_LEGACY, id);
    } catch { /* ignore */ }
    try { window.dispatchEvent(new Event("foyrmind:projectChanged")); } catch { /* ignore */ }
    nav("/", { replace: true });
  }

  function clearLinkedProjects(): void {
    try {
      localStorage.removeItem(env.VITE_LS_USER_PROJECTS);
      localStorage.removeItem(env.VITE_LS_INGEST_LATEST);
      localStorage.removeItem(env.VITE_LS_ACTIVE_PROJECT);
      localStorage.removeItem(env.VITE_LS_ACTIVE_PROJECT_LEGACY);
    } catch { /* ignore */ }
    try { window.dispatchEvent(new Event("foyrmind:projectChanged")); } catch { /* ignore */ }
  }

  function loadFoyrSample(): void {
    // CLAUDE.md §1 — sample is synthesised from public info only,
    // every unverified field is `null` with provenance markers. We
    // do NOT pretend to read Foyr Neo's protected project file.
    const text = JSON.stringify(foyrSample, null, 2);
    setBrief(text);
    setPlan(null);
    setUploadInfo({ name: "foyr-classic-living-office.json", bytes: text.length });
    setUploadError(null);
    runDetection(text, "foyr-classic-living-office.json", text.length);
    setMagicKey(`magic-${Date.now()}`);
  }

  async function readBriefFile(file: File): Promise<void> {
    setUploadError(null);
    if (file.size > MAX_BRIEF_BYTES) {
      setUploadError(`${file.name} is ${(file.size / 1024).toFixed(0)} KB — over the ${(MAX_BRIEF_BYTES / 1024).toFixed(0)} KB limit. Paste the relevant section or split the file.`);
      return;
    }
    const isText = TEXT_FILE_RE.test(file.name) || file.type.startsWith("text/") || file.type === "application/json";
    if (!isText) {
      setUploadError(`Can't parse ${file.name} in the browser. Accepted client-side: .txt, .md, .csv, .json, .tsv, .log, .html, .rtf. For .pdf / .docx, paste the text or use the server-side MinerU ingest route.`);
      return;
    }
    try {
      const text = await file.text();
      setBrief(text);
      setPlan(null);
      setAnswers({});
      setUploadInfo({ name: file.name, bytes: file.size });
      runDetection(text, file.name, file.size);
    } catch (err) {
      setUploadError(`Couldn't read ${file.name}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void readBriefFile(file);
  }

  function onFilePick(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) void readBriefFile(file);
    // Reset so picking the same file twice still fires.
    e.target.value = "";
  }

  async function runExtractor(): Promise<void> {
    if (!brief.trim()) return;
    setWorking(true);
    setPlan(null);
    setAnswers({});
    // Tiny delay so the spinner reads as work, not a blink.
    await new Promise<void>((r) => setTimeout(r, 350));
    setPlan(extractFromBrief(brief));
    setWorking(false);
  }

  const allAnswered = useMemo(() => {
    if (!plan) return false;
    return plan.clarifications.every((q) => (answers[q.for_field_label] ?? "").trim().length > 0);
  }, [plan, answers]);

  function commit(): void {
    // Real impl would POST to /api/ingest with plan + answers.
    // For the demo: stash in localStorage and route to the cockpit.
    if (!plan) return;
    const final = {
      plan_id: plan.plan_id,
      resolved: plan.resolved.map((r) =>
        r.value
          ? r
          : { ...r, value: answers[r.label] ?? "", confidence: 1, band: "high" as ConfidenceBand },
      ),
      sources: plan.sources,
    };
    try { localStorage.setItem("foyrmind.ingest.latest", JSON.stringify(final)); } catch { /* ignore */ }
    nav("/", { replace: true });
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-10">
      <header className="mb-6">
        <div className="eyebrow inline-flex items-center gap-1.5">
          <Wand2 className="w-3 h-3" /> Onboard a new project
        </div>
        <h1 className="display-2 mt-1 text-ink-950">Tell us about the project. We'll do the rest.</h1>
        <p className="text-[13px] text-ink-500 mt-1 max-w-3xl">
          Drop the client's brief, a costing sheet, or a Foyr export. We auto-detect the shape, fill in
          everything we're confident about, and only ask you about the few fields we can't infer —
          never the whole questionnaire.
        </p>
      </header>

      <DemoRunButton />

      <IntakePathPicker
        detection={detection}
        onLoadBriefSample={loadSample}
        onLoadFoyrSample={loadFoyrSample}
        onImportFoyrUrl={importFoyrFromUrl}
      />

      <LinkedProjectsToolbar onClear={clearLinkedProjects} />

      <OnboardingMagic playKey={magicKey} onCreate={commitFoyrSample} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="panel p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-foyr" />
              <span className="font-semibold text-ink-950 text-sm">Brief</span>
              <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">free text · costing · PDF</span>
            </div>
            <button onClick={loadSample} className="btn btn-outline !text-[10.5px] !py-1">
              <MessageSquareQuote className="w-3 h-3" /> Load sample
            </button>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={cls(
              "relative rounded-lg transition-shadow",
              dragOver && "ring-2 ring-foyr ring-offset-2 ring-offset-cream",
            )}
          >
            <textarea
              value={brief}
              onChange={(e) => {
                setBrief(e.target.value);
                runDetection(e.target.value, "pasted", e.target.value.length);
              }}
              placeholder="Paste the customer's brief, or drag a .txt / .md / .csv / .json file here…"
              className="w-full min-h-[300px] bg-cream border border-border rounded-lg p-3 text-[13.5px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-foyr/30 font-mono"
            />
            {dragOver ? (
              <div className="absolute inset-0 rounded-lg bg-foyr/10 grid place-items-center pointer-events-none">
                <div className="text-foyr-deep text-sm font-semibold inline-flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Drop file to load brief
                </div>
              </div>
            ) : null}
          </div>
          {uploadInfo ? (
            <div className="mt-2 text-[11px] text-ink-500 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-fern" /> Loaded <span className="text-ink-950 font-medium">{uploadInfo.name}</span> · {(uploadInfo.bytes / 1024).toFixed(1)} KB
            </div>
          ) : null}
          {uploadError ? (
            <div className="mt-2 text-[11px] text-foyr-deep inline-flex items-start gap-1.5 bg-foyr/5 border border-foyr/20 rounded px-2 py-1.5">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {uploadError}
            </div>
          ) : null}
          {detection ? <DetectionChip d={detection} /> : null}
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.markdown,.csv,.json,.tsv,.log,.html,.htm,.rtf,text/*,application/json"
            onChange={onFilePick}
            className="hidden"
          />
          <div className="flex items-center justify-between mt-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 text-[11px] text-ink-700 hover:text-ink-950 transition"
            >
              <Upload className="w-3 h-3" /> Drag here or browse
              <span className="text-ink-400 font-mono">.txt · .md · .csv · .json</span>
            </button>
            <button onClick={runExtractor} disabled={!brief.trim() || working} className="btn btn-foyr !text-xs !py-2">
              <Sparkles className={cls("w-3.5 h-3.5", working && "animate-pulse")} />
              {working ? "Reading…" : "Read the brief"}
            </button>
          </div>
        </section>

        <section>
          <AnimatePresence mode="wait">
            {plan ? (
              <PlanView
                key={plan.plan_id}
                plan={plan}
                answers={answers}
                onAnswerChange={(label, v) => setAnswers((a) => ({ ...a, [label]: v }))}
                onCommit={commit}
                canCommit={allAnswered || plan.clarifications.length === 0}
              />
            ) : (
              <PlaceholderView key="ph" working={working} />
            )}
          </AnimatePresence>
        </section>
      </div>
    </div>
  );
}

function PlaceholderView({ working }: { working: boolean }): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="panel p-6 border-dashed text-[12.5px] text-ink-500 min-h-[300px] flex items-center justify-center"
    >
      {working ? (
        <div className="space-y-2 text-center">
          <Sparkles className="w-5 h-5 text-foyr mx-auto animate-pulse" />
          <div>Reading the brief…</div>
        </div>
      ) : (
        <div className="space-y-2 text-center max-w-xs">
          <Wand2 className="w-5 h-5 text-foyr mx-auto" />
          <div>
            Paste a brief on the left and press <strong className="text-ink-950">Read the brief</strong>.
            We'll show you what we understood and what we still need to ask.
          </div>
        </div>
      )}
    </motion.div>
  );
}

function PlanView({
  plan, answers, onAnswerChange, onCommit, canCommit,
}: {
  plan: IngestPlan;
  answers: Record<string, string>;
  onAnswerChange: (label: string, v: string) => void;
  onCommit: () => void;
  canCommit: boolean;
}): JSX.Element {
  const high = plan.resolved.filter((f) => f.band === "high").length;
  const medium = plan.resolved.filter((f) => f.band === "medium").length;
  const missing = plan.clarifications.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="space-y-3"
    >
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-ink-950 text-sm inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-fern" /> Auto-mapped {high + medium} of {REQUIRED_FIELDS.length}
          </div>
          <div className="text-[11px] text-ink-500 tabular">
            coverage {Math.round(plan.coverage * 100)}%
          </div>
        </div>
        <div className="h-1.5 bg-cream-dark rounded-full overflow-hidden">
          <div className="h-full bg-foyr rounded-full" style={{ width: `${Math.round(plan.coverage * 100)}%` }} />
        </div>
        <div className="grid grid-cols-1 gap-1 mt-3">
          {plan.resolved.map((f) => <ResolvedRow key={f.label} field={f} />)}
        </div>
      </div>

      {plan.clarifications.length > 0 ? (
        <div className="panel p-3">
          <div className="font-semibold text-ink-950 text-sm inline-flex items-center gap-1.5 mb-2">
            <CircleHelp className="w-4 h-4 text-foyr" /> Still need from you · {missing}
          </div>
          <div className="space-y-2">
            {plan.clarifications.map((q) => {
              const required = REQUIRED_FIELDS.find((r) => r.label === q.for_field_label);
              return (
                <div key={q.question_id} className="border border-border rounded-lg p-2.5 bg-cream-dark/40">
                  <div className="text-[12.5px] text-ink-950 font-medium">{q.prompt}</div>
                  <div className="text-[10.5px] text-ink-500 mt-0.5">{q.why}</div>
                  <ClarificationInput
                    field={required}
                    value={answers[q.for_field_label] ?? ""}
                    onChange={(v) => onAnswerChange(q.for_field_label, v)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <button
        onClick={onCommit}
        disabled={!canCommit}
        className="btn btn-foyr w-full !py-2.5 !text-sm"
      >
        Create the project
        <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

function DetectionChip({ d }: { d: IntakeDetection }): JSX.Element {
  const palette =
    d.path === "foyr-export"    ? { bg: "bg-foyr/10",   text: "text-foyr-deep", border: "border-foyr/30" } :
    d.path === "json-heuristic" ? { bg: "bg-sky-50",    text: "text-sky-800",   border: "border-sky-200" } :
    d.path === "brief"          ? { bg: "bg-fern/10",   text: "text-fern",      border: "border-fern/30" } :
                                  { bg: "bg-cream-dark", text: "text-ink-700",  border: "border-border"  };
  const label =
    d.path === "foyr-export"    ? "Detected: Foyr Neo-shape · gated" :
    d.path === "json-heuristic" ? "Detected: typed JSON" :
    d.path === "brief"          ? "Detected: brief / free text" :
                                  "Shape not recognised yet";
  return (
    <div className={cls("mt-2 rounded-lg border px-2.5 py-2 text-[11px] leading-snug", palette.bg, palette.text, palette.border)}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{label}</span>
        <span className="pill bg-cream/80 border border-current/30 tabular text-[10px]">{Math.round(d.confidence * 100)}%</span>
      </div>
      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-ink-700/90">
        {d.reasons.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
      </ul>
      {d.gated ? (
        <div className="mt-1.5 text-[10.5px] text-foyr-deep">
          We won't fake-parse Foyr Neo's file. <a href={`mailto:${env.VITE_CONTACT_EMAIL}?subject=Share%20a%20Foyr%20Neo%20export`} className="underline underline-offset-2">Share one real export</a> and we ship the connector in ~2 weeks.
        </div>
      ) : null}
    </div>
  );
}

function ResolvedRow({ field }: { field: IngestField }): JSX.Element {
  const tone =
    field.band === "high"   ? "text-fern bg-fern/10 border-fern/30"   :
    field.band === "medium" ? "text-sea bg-sea/10 border-sea/30"      :
    field.band === "low"    ? "text-mulberry bg-mulberry/10 border-mulberry/30" :
                              "text-ink-500 bg-ink-100/40 border-border";
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 px-1 border-b border-border/40 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] text-ink-950 truncate">{field.label}</div>
      </div>
      <div className="text-right">
        <div className="text-[12.5px] text-ink-950 truncate max-w-[180px]">
          {field.value ?? <span className="text-ink-500 italic">(missing)</span>}
        </div>
        <span className={cls("pill text-[9.5px] border tabular", tone)}>
          {field.band} · {Math.round(field.confidence * 100)}%
        </span>
      </div>
    </div>
  );
}

function ClarificationInput({
  field, value, onChange,
}: {
  field: { format: string; choices?: readonly string[] } | undefined;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  if (!field) {
    return (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-cream border border-border rounded px-2 py-1 text-[12.5px]"
      />
    );
  }
  if (field.format === "single_choice" && field.choices) {
    return (
      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        {field.choices.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className={cls(
              "pill text-[11px] border",
              value === c
                ? "bg-ink-950 text-cream border-ink-950"
                : "bg-cream border-border text-ink-700 hover:border-foyr/40",
            )}
          >
            {c}
          </button>
        ))}
      </div>
    );
  }
  if (field.format === "multi_choice" && field.choices) {
    const set = new Set(value.split(",").map((s) => s.trim()).filter(Boolean));
    return (
      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        {field.choices.map((c) => {
          const on = set.has(c);
          return (
            <button
              key={c}
              type="button"
              onClick={() => {
                if (on) set.delete(c); else set.add(c);
                onChange(Array.from(set).join(", "));
              }}
              className={cls(
                "pill text-[11px] border",
                on
                  ? "bg-ink-950 text-cream border-ink-950"
                  : "bg-cream border-border text-ink-700 hover:border-foyr/40",
              )}
            >
              {c}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <input
      type={field.format === "number" ? "number" : field.format === "date" ? "date" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full bg-cream border border-border rounded px-2 py-1 text-[12.5px]"
    />
  );
}

/* Intake path picker — two clear paths.
 * Brief / costing / PDF and JSON autodetect (with the Foyr sample
 * as its primary "try it" CTA). The earlier "Foyr Neo bundle"
 * roadmap card was removed — JSON autodetect already routes the
 * Foyr sample through the same heuristic path. */
function IntakePathPicker({
  detection,
  onLoadBriefSample,
  onLoadFoyrSample,
  onImportFoyrUrl,
}: {
  detection: IntakeDetection | null;
  onLoadBriefSample: () => void;
  onLoadFoyrSample: () => void;
  onImportFoyrUrl: (id: string, url: string, meta: { name: string; client: string; city: string; bhk: string; budgetINR: number; deadline: string }) => void;
}): JSX.Element {
  const path = detection?.path;
  return (
    <section className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
      <PathCard
        active={path === "brief"}
        kind="brief"
        title="Brief / costing / PDF"
        sub="Ships today"
        body="Paste a written brief, drop a costing sheet, or feed a PDF you have on file. We map fields to the typed graph with one confidence band per field."
        confidence={path === "brief" ? detection!.confidence : null}
        cta={{ label: "Load a sample brief", onClick: onLoadBriefSample }}
        icon={<FileText className="w-4 h-4" />}
      />
      <PathCard
        active={path === "json-heuristic" || path === "foyr-export"}
        kind="json"
        title="JSON autodetect"
        sub="Heuristic walker"
        body={`Pattern-matches typed fields (rooms[], items[], placements, moodboard, budget) above the ${Math.round(env.VITE_INTAKE_CONFIDENCE_FLOOR * 100)}% confidence floor. One-click loads a real Foyr Neo scene.`}
        confidence={path === "json-heuristic" || path === "foyr-export" ? detection!.confidence : null}
        cta={{ label: "Load the Foyr sample", onClick: onLoadFoyrSample }}
        icon={<Workflow className="w-4 h-4" />}
      />
      <FoyrImportCard onImport={onImportFoyrUrl} />
    </section>
  );
}

/** Lightweight toolbar above the brief panel — shows how many
 *  linked Foyr projects exist locally + a one-click clear so the
 *  designer can re-import with the same name during the POC. */
function LinkedProjectsToolbar({ onClear }: { onClear: () => void }): JSX.Element | null {
  const [count, setCount] = useState<number>(() => readLinkedCount());

  function readLinkedCount(): number {
    if (typeof window === "undefined") return 0;
    try {
      const raw = window.localStorage.getItem(env.VITE_LS_USER_PROJECTS);
      if (!raw) return 0;
      const arr = JSON.parse(raw) as unknown[];
      return Array.isArray(arr) ? arr.length : 0;
    } catch { return 0; }
  }

  function reset(): void {
    onClear();
    setCount(0);
  }

  if (count === 0) return null;
  return (
    <div className="mb-4 rounded-xl border border-border bg-cream-dark/40 px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
      <div className="text-[11.5px] text-ink-700 leading-snug">
        <span className="font-semibold text-ink-950">{count}</span> linked Foyr project{count === 1 ? "" : "s"} on this device.
        Linked projects merge into the cockpit's project switcher with whatever name you chose.
      </div>
      <button
        type="button"
        onClick={reset}
        className="text-[11.5px] font-medium text-foyr-deep hover:underline underline-offset-2 inline-flex items-center gap-1.5"
      >
        <AlertTriangle className="w-3 h-3" /> Clear linked projects on this device
      </button>
    </div>
  );
}

/* Parse the Foyr Neo editor URL to extract the project id.
 * Verified live: the editor URL carries ?project={24-hex-ObjectId}.
 * No regex — uses the native URL API + char-range iteration. */
function isObjectId(id: string): boolean {
  if (id.length !== 24) return false;
  for (let i = 0; i < 24; i++) {
    const c = id.charCodeAt(i);
    const isDigit = c >= 48 && c <= 57;          // 0-9
    const isLowerHex = c >= 97 && c <= 102;      // a-f
    const isUpperHex = c >= 65 && c <= 70;       // A-F
    if (!isDigit && !isLowerHex && !isUpperHex) return false;
  }
  return true;
}

function parseFoyrEditorUrl(raw: string): { id: string; clean_url: string } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    const id = u.searchParams.get("project");
    if (id && isObjectId(id)) {
      const clean = new URL(`${u.origin}${u.pathname}?project=${id.toLowerCase()}`);
      return { id: id.toLowerCase(), clean_url: clean.toString() };
    }
    return null;
  } catch { return null; }
}

/** Parse a user-typed currency string into a positive integer ₹
 *  amount. No regex — iterates code points and keeps only digits,
 *  then trusts native `Number()` parsing. Handles "₹14,00,000",
 *  "1.4M" (treated as digits 14), "abc" (returns 0). */
function parseINR(input: string): number {
  if (!input) return 0;
  let digits = "";
  for (const ch of input) {
    const c = ch.charCodeAt(0);
    if (c >= 48 && c <= 57) digits += ch;
  }
  if (!digits) return 0;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function FoyrImportCard({ onImport }: { onImport: (id: string, url: string, meta: { name: string; client: string; city: string; bhk: string; budgetINR: number; deadline: string; bundleJson?: string }) => void }): JSX.Element {
  const bookmarklet = useMemo(() => buildFoyrCaptureBookmarklet(), []);
  // G.2 — push bookmarklet (the WRITE direction). Same install ritual
  // as the capture bookmarklet; the cockpit uses pushToFoyrNeo to
  // postMessage typed write actions into the Foyr Neo tab that has
  // this bridge installed.
  const pushBookmarklet = useMemo(
    () => buildFoyrNeoPushBookmarklet(typeof window !== "undefined" ? window.location.origin : "https://foyrmind.hypergraphmind.com"),
    [],
  );
  const [bundleJson, setBundleJson] = useState<string>("");
  const bundleStats = useMemo(() => {
    if (!bundleJson.trim()) return null;
    try {
      const obj = JSON.parse(bundleJson);
      if (!obj || typeof obj !== "object") return { ok: false, msg: "Not JSON." };
      const top = Object.keys(obj);
      const hasFoyrShape = top.includes("assetGroupList") || top.includes("sceneData") || top.includes("neoProductList");
      return { ok: hasFoyrShape, top, len: bundleJson.length };
    } catch { return { ok: false, msg: "Couldn't parse the JSON — try the bookmarklet again." }; }
  }, [bundleJson]);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [city, setCity] = useState("");
  const [bhk, setBhk] = useState("");
  const [budget, setBudget] = useState("");
  const [deadline, setDeadline] = useState("");
  const parsed = parseFoyrEditorUrl(url);
  const ready = !!parsed;
  const err = url.trim() && !parsed
    ? "Doesn't look like a Foyr Neo editor URL — open one of your projects on neo.foyr.com and copy the address bar here."
    : null;

  const allRequired = !!parsed && name.trim() && client.trim() && city.trim();
  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (!allRequired) return;
    const budgetINR = parseINR(budget);
    onImport(parsed!.id, parsed!.clean_url, {
      name: name.trim(),
      client: client.trim(),
      city: city.trim(),
      bhk: bhk.trim() || "—",
      budgetINR,
      deadline: deadline.trim(),
      ...(bundleJson.trim() ? { bundleJson } : {}),
    });
  }

  return (
    <article className="rounded-2xl border p-4 transition-all flex flex-col gap-2 border-foyr/30 bg-foyr/5 ring-1 ring-foyr/30 md:col-span-1">
      <div className="flex items-center gap-2">
        <span className="inline-flex w-7 h-7 rounded-lg border border-foyr/30 bg-foyr/10 text-foyr-deep items-center justify-center">
          <ShieldCheck className="w-4 h-4" />
        </span>
        <div className="leading-tight">
          <div className="text-[13.5px] font-semibold text-ink-950">Import from Foyr Neo</div>
          <div className="text-[10.5px] uppercase tracking-wider font-mono text-ink-500">Paste your editor URL</div>
        </div>
      </div>
      <p className="text-[11.5px] text-ink-700 leading-snug">
        Two options, both real-world:
      </p>
      <ul className="text-[11.5px] text-ink-700 leading-snug list-disc pl-4 space-y-1">
        <li>
          <a href="/extension/" target="_blank" rel="noreferrer" className="text-foyr-deep font-semibold hover:underline underline-offset-2">
            Install the Foyr Neo bridge ↗
          </a>{" "}
          — one-time Chrome extension, then every project ingests automatically.
        </li>
        <li>
          Or paste your Foyr Neo editor URL below and we link the project name only.
        </li>
      </ul>
      <form onSubmit={submit} className="space-y-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste your Foyr Neo editor URL"
          className="w-full bg-cream border border-border rounded-lg px-2.5 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
        />
        {err ? (
          <div className="text-[10.5px] text-foyr-deep inline-flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {err}
          </div>
        ) : null}
        {ready ? (
          <>
            <div className="text-[10.5px] text-fern inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Foyr project recognised — fill in the basics so the cockpit hero reads true.
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name · e.g. Mehta · Indiranagar Loft"
              autoFocus
              className="w-full bg-cream border border-border rounded-lg px-2.5 py-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
            />
            <div className="grid grid-cols-2 gap-1.5">
              <input
                type="text"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="Client · required"
                required
                className="bg-cream border border-border rounded-lg px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
              />
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City · required"
                required
                className="bg-cream border border-border rounded-lg px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
              />
              <input
                type="text"
                value={bhk}
                onChange={(e) => setBhk(e.target.value)}
                placeholder="Config · optional"
                className="bg-cream border border-border rounded-lg px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
              />
              <input
                type="text"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="Budget ₹ · optional"
                inputMode="numeric"
                className="bg-cream border border-border rounded-lg px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
              />
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="col-span-2 bg-cream border border-border rounded-lg px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
              />
            </div>
            <div className="text-[10px] text-ink-500 italic leading-snug">
              <span className="text-foyr-deep font-medium">Client</span> and <span className="text-foyr-deep font-medium">City</span> required.
            </div>

            <div className="rounded-lg border border-foyr/30 bg-cream/60 p-2.5 space-y-1.5">
              <div className="text-[10.5px] uppercase tracking-wider font-mono text-foyr-deep">
                Capture the real Foyr bundle (one-time, optional but recommended)
              </div>
              <div className="text-[11px] text-ink-700 leading-snug">
                Drag this link to your bookmarks bar. Then on any Foyr Neo editor tab, click it to copy your scene's bundle to the clipboard:
              </div>
              <div className="flex flex-wrap gap-1.5">
                <a
                  href={bookmarklet}
                  onClick={(e) => e.preventDefault()}
                  draggable
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-ink-950 text-cream text-[11px] font-semibold cursor-grab select-none"
                  title="Drag to your bookmarks bar. Click on any Foyr Neo editor tab to copy your scene's bundle to the clipboard."
                >
                  <Clipboard className="w-3 h-3" /> Capture Foyr bundle
                </a>
                <a
                  href={pushBookmarklet}
                  onClick={(e) => e.preventDefault()}
                  draggable
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-foyr/40 bg-foyr/10 text-foyr-deep text-[11px] font-semibold cursor-grab select-none"
                  title="Drag to your bookmarks bar. Click on the Foyr Neo editor tab once per session to accept typed write-back actions (move / rotate / place / apply finish) from the cockpit."
                >
                  <ArrowRight className="w-3 h-3" /> Install push bridge
                </a>
              </div>
              <textarea
                value={bundleJson}
                onChange={(e) => setBundleJson(e.target.value)}
                placeholder="Paste the captured bundle JSON here (the bookmarklet copied it for you)…"
                className="w-full h-20 bg-cream border border-border rounded-md px-2 py-1.5 text-[10.5px] font-mono focus:outline-none focus:ring-2 focus:ring-foyr/30"
              />
              {bundleStats?.ok ? (
                <div className="text-[10.5px] text-fern inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3" /> Foyr bundle recognised · {(bundleStats.len! / 1024).toFixed(1)} KB · top keys: {bundleStats.top!.slice(0, 4).join(", ")}
                </div>
              ) : bundleStats && !bundleStats.ok ? (
                <div className="text-[10.5px] text-foyr-deep inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> {bundleStats.msg ?? "Not a Foyr export — missing assetGroupList / sceneData."}
                </div>
              ) : (
                <div className="text-[10px] text-ink-500 italic">
                  No bundle → cockpit shows reasoning from your metadata. Paste a bundle → cockpit ingests the real typed graph.
                </div>
              )}
            </div>
          </>
        ) : null}
        <button
          type="submit"
          disabled={!allRequired}
          className="btn btn-foyr w-full !text-[12px] !py-2 inline-flex items-center justify-center gap-1.5"
        >
          <ArrowRight className="w-3 h-3" />
          {!ready ? "Paste a URL to continue" : !name.trim() ? "Name the project" : (!client.trim() || !city.trim()) ? "Add client + city" : "Create & open the project"}
        </button>
      </form>
    </article>
  );
}

function PathCard({
  active, kind, title, sub, body, confidence, cta, icon,
}: {
  active: boolean;
  kind: "brief" | "json" | "foyr";
  title: string;
  sub: string;
  body: string;
  confidence: number | null;
  cta: { label: string; onClick: () => void };
  icon: JSX.Element;
}): JSX.Element {
  const tone =
    kind === "foyr"  ? "border-foyr/30 bg-foyr/5"  :
    kind === "json"  ? "border-sky-200 bg-sky-50/40" :
                       "border-fern/30 bg-fern/5";
  const accent =
    kind === "foyr" ? "text-foyr-deep" :
    kind === "json" ? "text-sky-800"   :
                      "text-fern";
  return (
    <article className={cls(
      "rounded-2xl border p-4 transition-all flex flex-col gap-2",
      active ? `${tone} shadow-[0_4px_18px_-8px_rgba(0,0,0,0.18)] ring-1 ring-foyr/30` : "border-border bg-cream",
    )}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={cls("inline-flex w-7 h-7 rounded-lg border items-center justify-center", active ? `${tone} ${accent}` : "bg-cream-dark border-border text-ink-700")}>
            {icon}
          </span>
          <div className="leading-tight">
            <div className="text-[13.5px] font-semibold text-ink-950">{title}</div>
            <div className="text-[10.5px] uppercase tracking-wider font-mono text-ink-500">{sub}</div>
          </div>
        </div>
        {confidence !== null ? (
          <span className={cls("pill text-[10px] tabular", active ? `bg-cream ${accent} border border-current/30` : "bg-cream-dark border border-border text-ink-700")}>
            {Math.round(confidence * 100)}% match
          </span>
        ) : null}
      </div>
      <p className="text-[11.5px] text-ink-700 leading-snug">{body}</p>
      <button
        onClick={cta.onClick}
        className={cls(
          "mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium",
          accent, "hover:underline underline-offset-2",
        )}
      >
        {kind === "foyr" ? <Mail className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />} {cta.label}
      </button>
    </article>
  );
}
